<template>
  <div class="ls-compare-page">
    <!-- Header -->
    <div class="ls-header">
      <h1 class="ls-title">
        <span class="ls-icon">📊</span>
        Long/Short Ratio — Data Comparison
      </h1>
    </div>

    <!-- Control Panel -->
    <div class="ls-controls">
      <div class="ctrl-group">
        <label class="ctrl-label">Chế độ lấy mẫu</label>
        <div class="mode-buttons">
          <button :class="['mode-btn', selectedMode === '5m' ? 'active' : '']" @click="selectedMode = '5m'">
            <span class="mode-badge">TH1</span>
            24h · 5m / record (288 records)
          </button>
          <button :class="['mode-btn', selectedMode === '15m' ? 'active' : '']" @click="selectedMode = '15m'">
            <span class="mode-badge">TH2</span>
            3 ngày · 15m / record (288 records)
          </button>
        </div>
      </div>
      <div class="ctrl-actions">
        <button class="btn-analyze" :disabled="loading" @click="runAnalysis">
          <span v-if="!loading">⚡ Tiến hành Phân tích</span>
          <span v-else class="spinner-wrap">
            <span class="spinner"></span> Đang phân tích...
          </span>
        </button>
        <button class="btn-excel" :disabled="!results.length" @click="exportExcel">
          📥 Xuất file Excel
        </button>
      </div>
    </div>

    <!-- Summary Cards -->
    <div class="summary-cards" v-if="results.length || globalAvg !== null">
      <div class="summary-card">
        <div class="sc-label">Tổng coin đã phân tích</div>
        <div class="sc-value neutral">{{ results.length }}</div>
      </div>
      <div class="summary-card">
        <div class="sc-label">Chênh lệch TB toàn hệ thống</div>
        <div class="sc-value" :class="globalAvg >= 0 ? 'positive' : 'negative'">
          {{ globalAvg !== null ? (globalAvg >= 0 ? '+' : '') + globalAvg.toFixed(3) + '%' : '—' }}
        </div>
      </div>
      <div class="summary-card">
        <div class="sc-label">Timeframe</div>
        <div class="sc-value neutral">{{ selectedMode === '5m' ? '5 phút · 24h' : '15 phút · 3 ngày' }}</div>
      </div>
    </div>

    <!-- Search & Sort bar -->
    <div class="table-toolbar" v-if="results.length">
      <input v-model="search" class="search-input" placeholder="🔍 Tìm coin..." />
      <div class="sort-btns">
        <button :class="['sort-btn', sortKey === 'avgDiffPercent' ? 'active' : '']" @click="toggleSort('avgDiffPercent')">
          Chênh lệch TB {{ sortKey === 'avgDiffPercent' ? (sortDir === 'desc' ? '▼' : '▲') : '' }}
        </button>
        <button :class="['sort-btn', sortKey === 'avgTopAccounts' ? 'active' : '']" @click="toggleSort('avgTopAccounts')">
          Top Acc TB {{ sortKey === 'avgTopAccounts' ? (sortDir === 'desc' ? '▼' : '▲') : '' }}
        </button>
        <button :class="['sort-btn', sortKey === 'coinName' ? 'active' : '']" @click="toggleSort('coinName')">
          Coin {{ sortKey === 'coinName' ? (sortDir === 'desc' ? '▼' : '▲') : '' }}
        </button>
      </div>
    </div>

    <!-- Data Table -->
    <div class="ls-table-wrap" v-if="filteredResults.length">
      <table class="ls-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Coin</th>
            <th>TB Top Accounts</th>
            <th>TB Accounts</th>
            <th>Chênh lệch TB</th>
            <th>Chi tiết</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, i) in filteredResults" :key="row.coinName"
              :class="['ls-row', i % 2 === 0 ? 'even' : 'odd']">
            <td class="td-rank">{{ i + 1 }}</td>
            <td class="td-coin">
              <span class="coin-badge">{{ getDisplayName(row) }}</span>
            </td>
            <td class="td-num">{{ row.avgTopAccounts.toFixed(3) }}</td>
            <td class="td-num">{{ row.avgAccounts.toFixed(3) }}</td>
            <td class="td-diff">
              <span :class="['diff-badge', row.avgDiffPercent >= 0 ? 'pos' : 'neg']">
                {{ row.avgDiffPercent >= 0 ? '+' : '' }}{{ row.avgDiffPercent.toFixed(3) }}%
              </span>
            </td>
            <td class="td-action">
              <button class="btn-detail" @click="openDetail(row)">🔍 Xem</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Empty state -->
    <div class="ls-empty" v-if="!loading && !results.length">
      <div class="empty-icon">📈</div>
      <p>Chọn chế độ lấy mẫu và bấm <strong>Tiến hành Phân tích</strong> để bắt đầu.</p>
    </div>

    <!-- Detail Modal -->
    <div class="modal-overlay" v-if="detailCoin" @click.self="detailCoin = null">
      <div class="modal-box">
        <div class="modal-header">
          <span class="modal-coin">{{ getDisplayName(detailCoin) }}</span>
          <span class="modal-avg" :class="detailCoin.avgDiffPercent >= 0 ? 'positive' : 'negative'">
            TB Chênh lệch: {{ detailCoin.avgDiffPercent >= 0 ? '+' : '' }}{{ detailCoin.avgDiffPercent.toFixed(3) }}%
          </span>
          <button class="modal-close" @click="detailCoin = null">✕</button>
        </div>
        <div class="modal-body">
          <canvas ref="detailChart" class="detail-chart"></canvas>
          <div class="detail-table-wrap">
            <table class="detail-table">
              <thead>
                <tr>
                  <th>Thời gian</th>
                  <th>Top Accounts</th>
                  <th>Accounts</th>
                  <th>Chênh lệch %</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="rec in detailCoin.records" :key="rec.timestamp">
                  <td>{{ formatTs(rec.timestamp) }}</td>
                  <td>{{ rec.topValue.toFixed(3) }}</td>
                  <td>{{ rec.accValue.toFixed(3) }}</td>
                  <td :class="rec.diffPercent >= 0 ? 'pos-cell' : 'neg-cell'">
                    {{ rec.diffPercent >= 0 ? '+' : '' }}{{ rec.diffPercent.toFixed(3) }}%
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { API_URL } from '../config';
import {
  Chart as ChartJS,
  CategoryScale,
  TimeScale,
  LinearScale,
  LineElement,
  PointElement,
  LineController,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import 'chartjs-adapter-date-fns';

ChartJS.register(CategoryScale, TimeScale, LinearScale, LineElement, PointElement,
  LineController, Title, Tooltip, Legend, Filler);

export default {
  name: 'LongShortCompare',
  data() {
    return {
      selectedMode: '5m',
      loading: false,
      results: [],
      globalAvg: null,
      search: '',
      sortKey: 'avgDiffPercent',
      sortDir: 'desc',
      detailCoin: null,
      detailChartInstance: null,
    };
  },
  computed: {
    filteredResults() {
      const q = this.search.trim().toUpperCase();
      let list = q
        ? this.results.filter(r => {
            const coinName = (r.coinName || '').toUpperCase();
            const altName = (r.altName || '').toUpperCase();
            const displayName = this.getDisplayName(r).toUpperCase();
            return coinName.includes(q) || altName.includes(q) || displayName.includes(q);
          })
        : [...this.results];
      list.sort((a, b) => {
        let av = a[this.sortKey], bv = b[this.sortKey];
        if (this.sortKey === 'coinName') {
          av = this.getDisplayName(a);
          bv = this.getDisplayName(b);
        }
        if (typeof av === 'string') return this.sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
        return this.sortDir === 'asc' ? av - bv : bv - av;
      });
      return list;
    },
  },
  watch: {
    async detailCoin(coin) {
      if (!coin) {
        if (this.detailChartInstance) {
          this.detailChartInstance.destroy();
          this.detailChartInstance = null;
        }
        return;
      }
      await this.$nextTick();
      this.renderDetailChart(coin);
    },
  },
  methods: {
    getDisplayName(coin) {
      if (!coin) return '';
      return coin.altName ? `${coin.coinName}/${coin.altName}` : (coin.coinName || '');
    },

    toggleSort(key) {
      if (this.sortKey === key) this.sortDir = this.sortDir === 'desc' ? 'asc' : 'desc';
      else { this.sortKey = key; this.sortDir = 'desc'; }
    },

    async runAnalysis() {
      this.loading = true;
      this.results = [];
      this.globalAvg = null;
      try {
        const res = await fetch(`${API_URL}/binance/longshort-compare`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ period: this.selectedMode, limit: 288 }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        this.results = (data.coinComparisons || []).sort((a, b) => b.avgDiffPercent - a.avgDiffPercent);
        this.globalAvg = data.globalAverageDiffPercent ?? null;
      } catch (e) {
        console.error('Lỗi khi phân tích L/S:', e);
        alert('Có lỗi xảy ra khi phân tích. Vui lòng thử lại.');
      } finally {
        this.loading = false;
      }
    },

    openDetail(row) {
      this.detailCoin = row;
    },

    formatTs(ts) {
      if (!ts) return '';
      return new Date(ts).toLocaleString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' });
    },

    renderDetailChart(coin) {
      const canvas = this.$refs.detailChart;
      if (!canvas) return;
      if (this.detailChartInstance) this.detailChartInstance.destroy();

      const labels = (coin.records || []).map(r => new Date(r.timestamp));
      const topData = (coin.records || []).map(r => r.topValue);
      const accData = (coin.records || []).map(r => r.accValue);

      this.detailChartInstance = new ChartJS(canvas, {
        type: 'line',
        data: {
          labels,
          datasets: [
            {
              label: 'Top Accounts',
              data: topData,
              borderColor: '#7c3aed',
              backgroundColor: 'rgba(124,58,237,0.08)',
              borderWidth: 2,
              pointRadius: 0,
              fill: false,
              tension: 0.3,
            },
            {
              label: 'Accounts',
              data: accData,
              borderColor: '#10b981',
              backgroundColor: 'rgba(16,185,129,0.08)',
              borderWidth: 2,
              pointRadius: 0,
              fill: false,
              tension: 0.3,
            },
          ],
        },
        options: {
          responsive: true,
          interaction: { intersect: false, mode: 'index' },
          plugins: {
            legend: { display: true, position: 'top' },
            title: { display: true, text: `${this.getDisplayName(coin)} — L/S Ratio Comparison` },
            datalabels: { display: false },
            tooltip: {
              callbacks: {
                label: function (context) {
                  let label = context.dataset.label || '';
                  if (label) {
                    label += ': ';
                  }
                  if (context.parsed.y !== null) {
                    label += Number(context.parsed.y).toFixed(3);
                  }
                  return label;
                }
              }
            }
          },
          scales: {
            x: {
              type: 'time',
              time: { tooltipFormat: 'dd/MM HH:mm' },
              ticks: { maxTicksLimit: 10 },
            },
            y: { ticks: { precision: 3 } },
          },
        },
      });
    },

    exportExcel() {
      if (!this.results.length) return;

      const modeLabel = this.selectedMode === '5m' ? 'TH1_24h_5m' : 'TH2_3ngay_15m';
      const now = new Date().toLocaleString('vi-VN').replace(/[/:, ]/g, '_');

      // Build XML/HTML table (works as Excel)
      const rows = this.results.map(r => `
        <tr>
          <td>${this.getDisplayName(r)}</td>
          <td>${r.avgTopAccounts.toFixed(3)}</td>
          <td>${r.avgAccounts.toFixed(3)}</td>
          <td style="color:${r.avgDiffPercent >= 0 ? 'green' : 'red'};font-weight:bold">
            ${(r.avgDiffPercent >= 0 ? '+' : '') + r.avgDiffPercent.toFixed(3)}%
          </td>
        </tr>
      `).join('');

      const html = `
        <html xmlns:o="urn:schemas-microsoft-com:office:office"
              xmlns:x="urn:schemas-microsoft-com:office:excel"
              xmlns="http://www.w3.org/TR/REC-html40">
        <head><meta charset="UTF-8">
          <style>
            body{font-family:Arial,sans-serif;font-size:12px}
            table{border-collapse:collapse;width:100%}
            th{background:#1e293b;color:#fff;padding:8px 12px;text-align:center}
            td{padding:6px 10px;border:1px solid #e2e8f0;text-align:center}
            tr:nth-child(even){background:#f8fafc}
            .header-info{margin-bottom:12px;font-size:13px;color:#334155}
          </style>
        </head>
        <body>
          <div class="header-info">
            <strong>Long/Short Ratio Comparison — Binance Futures</strong><br>
            Chế độ: <strong>${this.selectedMode === '5m' ? 'TH1: 24h · 5m · 288 records' : 'TH2: 3 ngày · 15m · 288 records'}</strong>
            &nbsp;|&nbsp; Xuất lúc: <strong>${new Date().toLocaleString('vi-VN')}</strong>
            &nbsp;|&nbsp; Chênh lệch TB toàn hệ thống: <strong>${this.globalAvg !== null ? (this.globalAvg >= 0 ? '+' : '') + this.globalAvg.toFixed(3) + '%' : 'N/A'}</strong>
          </div>
          <table>
            <thead>
              <tr>
                <th>Coin</th>
                <th>TB Top Accounts</th>
                <th>TB Accounts</th>
                <th>Chênh lệch TB %</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
        </body>
        </html>
      `;

      const blob = new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `LS_Compare_${modeLabel}_${now}.xls`;
      a.click();
      URL.revokeObjectURL(url);
    },
  },
};
</script>

<style scoped>
/* ==================== GENERAL & LIGHT MODE (DEFAULT) ==================== */
.ls-compare-page {
  min-height: 85vh;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #f8fafc 100%);
  color: #0f172a;
  padding: 24px;
  font-family: 'Inter', 'Segoe UI', sans-serif;
  transition: background 0.3s, color 0.3s;
}

/* Header */
.ls-header { text-align: center; margin-bottom: 16px; }
.ls-title {
  font-size: 20px;
  font-weight: 800;
  background: linear-gradient(135deg, #7c3aed, #06b6d4);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin: 0 0 6px;
}
.ls-icon { font-size: 22px; margin-right: 6px; filter: none; -webkit-text-fill-color: initial; }

/* Controls */
.ls-controls {
  display: flex;
  align-items: flex-end;
  gap: 16px;
  flex-wrap: wrap;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px 18px;
  margin-bottom: 16px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  transition: background 0.3s, border-color 0.3s;
}
.ctrl-label { display: block; font-size: 11px; color: #64748b; margin-bottom: 6px; font-weight: 600; letter-spacing: 0.5px; }
.mode-buttons { display: flex; gap: 12px; }
.mode-btn {
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  color: #475569;
  padding: 7px 14px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
}
.mode-btn:hover { border-color: #7c3aed; color: #7c3aed; background: #faf5ff; }
.mode-btn.active {
  background: linear-gradient(135deg, rgba(124, 58, 237, 0.08), rgba(6, 182, 212, 0.04));
  border-color: #7c3aed;
  color: #7c3aed;
  font-weight: 600;
}
.mode-badge {
  background: #7c3aed;
  color: #fff;
  border-radius: 4px;
  padding: 1px 5px;
  font-size: 9px;
  font-weight: 700;
}
.ctrl-actions { display: flex; gap: 10px; margin-left: auto; align-items: flex-end; }
.btn-analyze {
  background: linear-gradient(135deg, #7c3aed, #06b6d4);
  border: none;
  border-radius: 8px;
  color: #fff;
  padding: 8px 20px;
  font-size: 12.5px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 12px rgba(124, 58, 237, 0.3);
}
.btn-analyze:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 20px rgba(124, 58, 237, 0.5); }
.btn-analyze:disabled { opacity: 0.55; cursor: not-allowed; transform: none; }
.btn-excel {
  background: linear-gradient(135deg, #059669, #10b981);
  border: none;
  border-radius: 8px;
  color: #fff;
  padding: 8px 18px;
  font-size: 12.5px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.25);
}
.btn-excel:hover:not(:disabled) { transform: translateY(-1px); }
.btn-excel:disabled { opacity: 0.4; cursor: not-allowed; }

.spinner-wrap { display: flex; align-items: center; gap: 8px; }
.spinner {
  width: 14px; height: 14px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  display: inline-block;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* Summary cards */
.summary-cards { display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 16px; }
.summary-card {
  flex: 1;
  min-width: 140px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px 16px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  transition: background 0.3s, border-color 0.3s;
}
.sc-label { font-size: 11px; color: #64748b; margin-bottom: 4px; font-weight: 600; }
.sc-value { font-size: 16px; font-weight: 800; }
.sc-value.neutral { color: #0f172a; }
.sc-value.positive { color: #059669; }
.sc-value.negative { color: #dc2626; }

/* Toolbar */
.table-toolbar { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-bottom: 12px; }
.search-input {
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  color: #0f172a;
  padding: 8px 14px;
  font-size: 13px;
  width: 200px;
  outline: none;
  transition: border-color 0.2s, background 0.3s;
}
.search-input:focus { border-color: #7c3aed; }
.search-input::placeholder { color: #94a3b8; }
.sort-btns { display: flex; gap: 8px; margin-left: auto; }
.sort-btn {
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 7px;
  color: #475569;
  padding: 6px 14px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s;
}
.sort-btn.active { border-color: #7c3aed; color: #7c3aed; background: rgba(124,58,237,0.08); font-weight: 600; }
.sort-btn:hover { color: #7c3aed; }

/* Table */
.ls-table-wrap {
  border-radius: 14px;
  overflow: auto;
  border: 1px solid #e2e8f0;
  max-height: 65vh;
  background: #ffffff;
  transition: background 0.3s, border-color 0.3s;
}
.ls-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.ls-table thead th {
  background: #f1f5f9;
  color: #475569;
  padding: 12px 14px;
  text-align: center;
  font-weight: 700;
  position: sticky;
  top: 0;
  z-index: 2;
  border-bottom: 2px solid #e2e8f0;
}
.ls-table tbody .ls-row.even { background: #f8fafc; }
.ls-table tbody .ls-row.odd { background: transparent; }
.ls-table tbody td { padding: 10px 14px; text-align: center; border-bottom: 1px solid #e2e8f0; transition: border-color 0.3s; }

.td-rank { color: #94a3b8; font-size: 12px; width: 42px; }
.td-coin { font-weight: 700; }
.coin-badge {
  background: #faf5ff;
  border: 1px solid #e9d5ff;
  border-radius: 6px;
  padding: 3px 10px;
  font-size: 12px;
  color: #7c3aed;
}
.td-num { font-family: monospace; color: #334155; }
.diff-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
}
.diff-badge.pos { background: #d1fae5; color: #065f46; }
.diff-badge.neg { background: #fee2e2; color: #991b1b; }

.btn-detail {
  background: #ecfeff;
  border: 1px solid #a5f3fc;
  border-radius: 7px;
  color: #0891b2;
  padding: 5px 12px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s;
}
.btn-detail:hover { background: #cffafe; }

/* Empty */
.ls-empty { text-align: center; padding: 60px 20px; color: #94a3b8; }
.empty-icon { font-size: 56px; margin-bottom: 16px; }

/* Modal */
.modal-overlay {
  position: fixed; inset: 0;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(4px);
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}
.modal-box {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  width: 100%;
  max-width: 920px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  transition: background 0.3s, border-color 0.3s;
}
.modal-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 24px;
  border-bottom: 1px solid #e2e8f0;
  flex-shrink: 0;
}
.modal-coin { font-size: 20px; font-weight: 800; color: #7c3aed; }
.modal-avg { font-size: 14px; font-weight: 700; margin-left: 4px; }
.modal-avg.positive { color: #059669; }
.modal-avg.negative { color: #dc2626; }
.modal-close {
  margin-left: auto;
  background: #f1f5f9;
  border: none;
  border-radius: 8px;
  color: #64748b;
  padding: 6px 12px;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.2s;
}
.modal-close:hover { color: #ef4444; background: #fee2e2; }
.modal-body { flex: 1; overflow-y: auto; padding: 20px 24px; display: flex; flex-direction: column; gap: 20px; }
.detail-chart { max-height: 280px; }
.detail-table-wrap { overflow: auto; border-radius: 10px; border: 1px solid #e2e8f0; }
.detail-table { width: 100%; border-collapse: collapse; font-size: 12px; }
.detail-table thead th {
  background: #f1f5f9;
  color: #475569;
  padding: 9px 12px;
  text-align: center;
  font-weight: 700;
}
.detail-table tbody td { padding: 7px 12px; text-align: center; border-bottom: 1px solid #e2e8f0; color: #334155; }
.pos-cell { color: #059669; font-weight: 600; }
.neg-cell { color: #dc2626; font-weight: 600; }


/* ==================== DARK MODE OVERRIDES ==================== */
body.dark-mode .ls-compare-page {
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%);
  color: #e2e8f0;
}

body.dark-mode .ls-controls {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.08);
  box-shadow: none;
}

body.dark-mode .ctrl-label {
  color: #94a3b8;
}

body.dark-mode .mode-btn {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: #94a3b8;
}

body.dark-mode .mode-btn:hover {
  border-color: #7c3aed;
  color: #c4b5fd;
  background: rgba(124, 58, 237, 0.08);
}

body.dark-mode .mode-btn.active {
  background: linear-gradient(135deg, rgba(124, 58, 237, 0.25), rgba(6, 182, 212, 0.15));
  border-color: #7c3aed;
  color: #c4b5fd;
}

body.dark-mode .summary-card {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.08);
  box-shadow: none;
}

body.dark-mode .sc-label {
  color: #94a3b8;
}

body.dark-mode .sc-value.neutral {
  color: #e2e8f0;
}

body.dark-mode .sc-value.positive {
  color: #34d399;
}

body.dark-mode .sc-value.negative {
  color: #f87171;
}

body.dark-mode .search-input {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.12);
  color: #e2e8f0;
}

body.dark-mode .search-input::placeholder {
  color: #64748b;
}

body.dark-mode .sort-btn {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.1);
  color: #94a3b8;
}

body.dark-mode .sort-btn.active {
  border-color: #7c3aed;
  color: #c4b5fd;
  background: rgba(124, 58, 237, 0.12);
}

body.dark-mode .sort-btn:hover {
  color: #c4b5fd;
}

body.dark-mode .ls-table-wrap {
  border-color: rgba(255, 255, 255, 0.08);
  background: transparent;
}

body.dark-mode .ls-table thead th {
  background: rgba(124, 58, 237, 0.2);
  color: #c4b5fd;
  border-bottom: none;
}

body.dark-mode .ls-table tbody .ls-row.even {
  background: rgba(255, 255, 255, 0.02);
}

body.dark-mode .ls-table tbody td {
  border-bottom-color: rgba(255, 255, 255, 0.04);
}

body.dark-mode .td-rank {
  color: #64748b;
}

body.dark-mode .coin-badge {
  background: rgba(124, 58, 237, 0.15);
  border-color: rgba(124, 58, 237, 0.3);
  color: #c4b5fd;
}

body.dark-mode .td-num {
  color: #cbd5e1;
}

body.dark-mode .diff-badge.pos {
  background: rgba(52, 211, 153, 0.15);
  color: #34d399;
}

body.dark-mode .diff-badge.neg {
  background: rgba(248, 113, 113, 0.15);
  color: #f87171;
}

body.dark-mode .btn-detail {
  background: rgba(6, 182, 212, 0.1);
  border-color: rgba(6, 182, 212, 0.3);
  color: #67e8f9;
}

body.dark-mode .btn-detail:hover {
  background: rgba(6, 182, 212, 0.2);
}

body.dark-mode .ls-empty {
  color: #64748b;
}

body.dark-mode .modal-overlay {
  background: rgba(0, 0, 0, 0.75);
}

body.dark-mode .modal-box {
  background: #1e293b;
  border-color: rgba(124, 58, 237, 0.3);
  box-shadow: 0 25px 60px rgba(0,0,0,0.6);
}

body.dark-mode .modal-header {
  border-bottom-color: rgba(255, 255, 255, 0.08);
}

body.dark-mode .modal-coin {
  color: #c4b5fd;
}

body.dark-mode .modal-avg.positive {
  color: #34d399;
}

body.dark-mode .modal-avg.negative {
  color: #f87171;
}

body.dark-mode .modal-close {
  background: rgba(255, 255, 255, 0.06);
  color: #94a3b8;
}

body.dark-mode .modal-close:hover {
  color: #f87171;
  background: rgba(248, 113, 113, 0.15);
}

body.dark-mode .detail-table-wrap {
  border-color: rgba(255, 255, 255, 0.08);
}

body.dark-mode .detail-table thead th {
  background: rgba(124, 58, 237, 0.15);
  color: #c4b5fd;
}

body.dark-mode .detail-table tbody td {
  border-bottom-color: rgba(255, 255, 255, 0.04);
  color: #cbd5e1;
}

body.dark-mode .pos-cell {
  color: #34d399;
}

body.dark-mode .neg-cell {
  color: #f87171;
}
</style>
